<template>
  <div>
    <!-- ============================================================ -->
    <!-- HERO                                                        -->
    <!-- ============================================================ -->
    <section class="relative bg-hero-fade pt-20 pb-12 lg:pt-24">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>

      <div class="relative container-wide">
        <div class="max-w-2xl mx-auto text-center">
          <div class="eyebrow mx-auto w-fit mb-4">Pricing</div>
          <h1 class="font-display text-5xl lg:text-6xl leading-[1.05] text-ink-900 text-balance">
            SEO <span class="italic text-brand-700">intelligence</span> that grows with you.
          </h1>
          <p class="mt-5 text-lg text-ink-600 text-pretty">
            From your first audit to managing hundreds of clients — one platform, four stages, zero lock-in.
          </p>

          <!-- BILLING TOGGLE -->
          <div class="mt-10 grid grid-cols-2 bg-ink-100/60 p-1 rounded-full relative w-fit mx-auto">
            <button
              @click="billing = 'monthly'"
              class="relative z-10 px-5 py-2 text-sm font-medium rounded-full transition-colors duration-200 flex items-center justify-center"
              :class="billing === 'monthly' ? 'text-ink-900' : 'text-ink-500 hover:text-ink-700'"
            >
              Monthly
            </button>
            <button
              @click="billing = 'yearly'"
              class="relative z-10 px-5 py-2 text-sm font-medium rounded-full transition-colors duration-200 flex items-center justify-center gap-2"
              :class="billing === 'yearly' ? 'text-ink-900' : 'text-ink-500 hover:text-ink-700'"
            >
              Yearly
              <span class="badge-accent !py-0 !px-1.5 !text-[10px]">Save 20%</span>
            </button>
            <div
              class="absolute top-1 bottom-1 bg-white rounded-full shadow-soft transition-all duration-300 ease-out"
              :style="togglePillStyle"
            ></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- EVIDENCE STRIP — original research, our own measured numbers  -->
    <!-- Every figure is from a real study we ran and published:       -->
    <!-- /learn/ai-crawler-access-australian-agencies                  -->
    <!-- ============================================================ -->
    <section class="pt-2 pb-2 lg:pb-4">
      <div class="container-wide max-w-5xl">
        <div class="rounded-2xl border border-ink-100 bg-white shadow-soft p-6 lg:p-7">
          <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div class="eyebrow mb-2.5"><Icon name="search" :size="12" /> Original research</div>
              <p class="font-display text-xl lg:text-2xl text-ink-900 tracking-tight text-balance">
                We scanned 191 Australian agency websites.
                <em class="italic text-brand-700">Here's what we found.</em>
              </p>
              <div class="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-ink-600">
                <span><span class="font-display text-lg text-ink-900">191</span> sites scanned</span>
                <span class="text-ink-300">·</span>
                <span><span class="font-display text-lg text-ink-900">5%</span> block at least one AI crawler</span>
                <span class="text-ink-300">·</span>
                <span><span class="font-display text-lg text-ink-900">49%</span> publish an llms.txt</span>
              </div>
              <p class="mt-2.5 text-xs text-ink-500">
                Agencies only. Counted from live robots.txt, September 2026. Method and full data published in the write-up.
              </p>
            </div>
            <router-link
              to="/learn/ai-crawler-access-australian-agencies"
              class="btn btn-outline btn-lg shrink-0 inline-flex items-center gap-2"
            >
              Read the study
              <Icon name="arrow-right" :size="14" />
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- PRICING TIERS — 4 cards                                      -->
    <!-- ============================================================ -->
    <section class="py-10 lg:py-14">
      <div class="container-wide max-w-7xl">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          <div
            v-for="tier in tiers"
            :key="tier.key"
            :id="tier.key"
            class="relative card gradient-brand-fade p-6 lg:p-7 transition-all duration-300 flex flex-col scroll-mt-20"
            :class="[
              tier.featured
                ? 'border border-brand-500 shadow-card md:scale-[1.03] z-10'
                : tier.key === 'starter'
                  ? 'border border-ink-200 card-hover'
                  : 'border border-ink-100 card-hover',
            ]"
          >
            <!-- Featured ribbon -->
            <div v-if="tier.featured" class="absolute -top-3 left-1/2 -translate-x-1/2">
              <span class="inline-flex items-center gap-1 bg-brand-600 text-white text-2xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                <Icon name="sparkles" :size="10" /> Most popular
              </span>
            </div>

            <!-- Starter tier badge -->
            <div v-if="tier.key === 'starter'" class="absolute -top-3 left-1/2 -translate-x-1/2">
              <span class="inline-flex items-center gap-1 bg-accent-500/90 text-white text-2xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                No credit card
              </span>
            </div>

            <!-- Header -->
            <div>
              <div class="text-xs font-semibold uppercase tracking-[0.14em]" :class="tier.key === 'starter' ? 'text-accent-700' : 'text-ink-500'">
                {{ tier.tag }}
              </div>
              <h3 class="font-display text-2xl lg:text-3xl text-ink-900 mt-1.5 tracking-tight">{{ tier.name }}</h3>
              <p class="mt-2 text-sm text-ink-600 leading-relaxed min-h-[2.6em]">{{ tier.desc }}</p>
            </div>

            <!-- Price -->
            <div class="mt-5 pb-5 border-b border-ink-100">
              <div v-if="tier.key === 'starter'" class="flex items-baseline gap-1">
                <span class="font-display text-5xl text-ink-900 tabular-nums tracking-tight">Free</span>
              </div>
              <div v-else-if="tier.customPrice" class="font-display text-3xl text-ink-900">Let's talk</div>
              <div v-if="tier.customPrice" class="mt-1.5 text-xs text-ink-500">Custom pricing tailored to your needs</div>
              <!-- USD stated explicitly. Stripe charges in the currency of
                   the Price objects (USD), and this page's own JSON-LD already
                   declares priceCurrency: 'USD' to search engines — but a bare
                   "$" left an international buyer no way to tell which dollar
                   they were agreeing to. -->
              <div v-else class="flex items-baseline gap-1">
                <span class="font-display text-5xl text-ink-900 tabular-nums tracking-tight">
                  ${{ currentPrice(tier) }}
                </span>
                <span class="text-sm text-ink-500">USD / mo</span>
              </div>
              <div v-if="billing === 'yearly' && !tier.customPrice && tier.key !== 'starter'" class="mt-1.5 flex items-center gap-1.5 text-xs text-accent-700">
                <Icon name="check" :size="12" />
                <span>${{ currentPrice(tier) * 12 }} USD/yr — save ${{ (tier.monthly - tier.yearly) * 12 }}</span>
              </div>
              <div v-else-if="!tier.customPrice && tier.key !== 'starter'" class="mt-1.5 text-xs text-ink-500">Billed monthly</div>
              <div v-else-if="tier.key === 'starter'" class="mt-1.5 text-xs text-ink-500">Forever free. No strings.</div>
            </div>

            <!-- CTA -->
            <router-link
              v-if="tier.key === 'starter'"
              to="/register"
              class="mt-5 w-full btn text-center btn-outline btn-lg border-accent-300 text-accent-700 hover:bg-accent-50"
            >
              {{ tier.cta.label }}
              <Icon name="arrow-right" :size="14" />
            </router-link>
            <button
              v-else-if="tier.customPrice"
              @click="router.push(tier.cta.to)"
              class="mt-5 w-full btn text-center btn-outline btn-lg"
            >
              {{ tier.cta.label }}
              <Icon name="arrow-right" :size="14" />
            </button>
            <button
              v-else
              @click="checkout(tier.key)"
              :disabled="checkoutLoading === tier.key"
              class="mt-5 w-full btn text-center"
              :class="tier.featured ? 'btn-brand btn-lg' : 'btn-outline btn-lg'"
            >
              <span v-if="checkoutLoading === tier.key">
                Processing…
              </span>
              <template v-else>
                {{ tier.cta.label }}
                <Icon name="arrow-right" :size="14" />
              </template>
            </button>

            <!-- Checkout error -->
            <p v-if="checkoutError && checkoutMessageTier === tier.key" class="mt-2 text-xs text-danger-600 text-center">{{ checkoutError }}</p>
            <!-- Immediate plan-change confirmation (existing subscriber, no redirect) -->
            <p v-if="checkoutSuccessMessage && checkoutMessageTier === tier.key" class="mt-2 text-xs text-accent-600 text-center">{{ checkoutSuccessMessage }}</p>

            <!-- Feature highlights -->
            <div class="mt-5 pt-1 flex-1">
              <div class="text-2xs font-bold uppercase tracking-wider text-ink-500 mb-3">What's included</div>
              <ul class="space-y-2.5">
                <li v-for="f in tier.highlights" :key="f" class="flex items-start gap-2 text-sm">
                  <Icon name="check-circle" :size="14" classes="text-accent-600 mt-0.5 shrink-0" />
                  <span class="text-ink-700">{{ f }}</span>
                </li>
              </ul>
            </div>

            <!-- Full feature list toggle -->
            <details class="mt-4 pt-3 border-t border-ink-100 group">
              <summary class="text-xs font-medium text-ink-500 hover:text-ink-700 cursor-pointer list-none flex items-center gap-1">
                All {{ tier.features.length }} features
                <Icon name="chevron-down" :size="12" classes="transition-transform group-open:rotate-180" />
              </summary>
              <ul class="mt-2 space-y-2">
                <li v-for="f in tier.features" :key="f.label" class="flex items-start gap-2.5 text-sm">
                  <Icon
                    :name="f.included ? 'check-circle' : 'x'"
                    :size="14"
                    :classes="f.included ? 'text-accent-600 mt-0.5 shrink-0' : 'text-ink-300 mt-0.5 shrink-0'"
                  />
                  <span :class="f.included ? 'text-ink-700' : 'text-ink-500 line-through'">
                    {{ f.label }}
                  </span>
                </li>
              </ul>
            </details>
          </div>
        </div>

        <p class="text-center text-xs text-ink-500 mt-8">
          All plans include TLS 1.3 encryption and bcrypt password hashing. Upgrade or downgrade anytime — prorated to the day.
        </p>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- FEATURE COMPARISON TABLE                                     -->
    <!-- ============================================================ -->
    <section class="py-16 lg:py-24 bg-ink-50/50 border-y border-ink-100">
      <div class="container-wide max-w-7xl">
        <div class="max-w-2xl mb-10">
          <div class="eyebrow">Full comparison</div>
          <h2 class="font-display text-4xl mt-3 text-ink-900 tracking-tight">Every feature, side by side.</h2>
        </div>

        <!-- Desktop / tablet: full side-by-side table -->
        <div class="hidden sm:block card overflow-hidden">
          <div class="overflow-x-auto scroll-custom">
            <table class="w-full text-sm min-w-[700px]">
              <thead class="bg-ink-50/60 border-b border-ink-100">
                <tr>
                  <th class="text-left px-5 py-4 font-semibold text-ink-700 min-w-[220px]">Feature</th>
                  <th v-for="tier in tiers" :key="tier.key" class="text-center px-4 py-4 font-semibold" :class="tier.featured ? 'text-ink-900' : 'text-ink-700'">
                    {{ tier.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in comparisonRows" :key="row.label"
                  class="border-b border-ink-100 last:border-0"
                  :class="row.header ? 'bg-ink-50/30' : ''"
                >
                  <td class="px-5 py-3.5" :class="row.header ? 'font-semibold text-ink-900 text-2xs uppercase tracking-[0.12em]' : 'text-ink-700'">
                    {{ row.label }}
                    <Tooltip v-if="row.tooltip && !row.header" :title="row.label" :body="row.tooltip" placement="right">
                      <button class="ml-1.5 text-ink-300 hover:text-ink-600 align-middle" aria-label="More info">
                        <Icon name="info" :size="12" />
                      </button>
                    </Tooltip>
                  </td>
                  <td v-for="tier in tiers" :key="tier.key + row.label" class="text-center px-4 py-3.5">
                    <template v-if="row.header"></template>
                    <span v-else-if="row.values[tier.key] === true" class="inline-flex w-5 h-5 rounded-full bg-accent-100 text-accent-700 items-center justify-center">
                      <Icon name="check" :size="12" strokeWidth="2.5" />
                    </span>
                    <span v-else-if="row.values[tier.key] === false" class="inline-flex w-5 h-5 rounded-full bg-ink-100 text-ink-500 items-center justify-center">
                      <Icon name="x" :size="10" strokeWidth="2.5" />
                    </span>
                    <span v-else class="text-ink-800 font-medium text-xs">{{ row.values[tier.key] }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Mobile: one tier per row doesn't fit the screen, so each tier
             becomes its own stacked, collapsible card instead of a
             horizontally-scrolling table nobody discovers they can swipe. -->
        <div class="sm:hidden space-y-3">
          <CollapsibleSection
            v-for="tier in tiers"
            :key="tier.key"
            :title="tier.name"
            :badge="tier.featured ? 'Most popular' : ''"
            badge-tone="accent"
            :default-open="tier.featured"
          >
            <div class="divide-y divide-ink-100">
              <template v-for="row in comparisonRows" :key="tier.key + row.label">
                <div v-if="row.header" class="pt-3 pb-1.5 text-2xs font-semibold text-ink-900 uppercase tracking-[0.12em] first:pt-0">
                  {{ row.label }}
                </div>
                <div v-else class="flex items-center justify-between gap-3 py-2 text-sm">
                  <span class="text-ink-600 min-w-0">{{ row.label }}</span>
                  <span v-if="row.values[tier.key] === true" class="inline-flex w-5 h-5 rounded-full bg-accent-100 text-accent-700 items-center justify-center shrink-0">
                    <Icon name="check" :size="12" strokeWidth="2.5" />
                  </span>
                  <span v-else-if="row.values[tier.key] === false" class="inline-flex w-5 h-5 rounded-full bg-ink-100 text-ink-500 items-center justify-center shrink-0">
                    <Icon name="x" :size="10" strokeWidth="2.5" />
                  </span>
                  <span v-else class="text-ink-800 font-medium text-xs shrink-0">{{ row.values[tier.key] }}</span>
                </div>
              </template>
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- FAQ                                                          -->
    <!-- ============================================================ -->
    <section class="py-20 lg:py-28 bg-hero-fade">
      <div class="container-wide max-w-3xl">
        <div class="text-center mb-12">
          <div class="eyebrow mx-auto w-fit">FAQ</div>
          <h2 class="font-display text-4xl mt-3 text-ink-900">Frequently asked</h2>
        </div>

        <div class="divide-y divide-ink-100 card !rounded-2xl">
          <details v-for="(faq, i) in faqs" :key="i" class="group p-5 lg:p-6">
            <summary class="flex items-start justify-between gap-4 cursor-pointer list-none">
              <span class="text-base font-medium text-ink-900">{{ faq.question }}</span>
              <span class="mt-0.5 shrink-0 w-7 h-7 rounded-full bg-ink-100 text-ink-600 flex items-center justify-center transition-transform duration-200 group-open:rotate-45">
                <Icon name="plus" :size="14" />
              </span>
            </summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed pr-10">
              {{ faq.answer }}
            </p>
          </details>
        </div>
      </div>
    </section>

    <!-- ============================================================ -->
    <!-- FINAL CTA                                                    -->
    <!-- ============================================================ -->
    <section class="pb-24">
      <div class="container-tight">
        <div class="card p-10 lg:p-14 text-center relative overflow-hidden bg-ink-900 border border-ink-800">
          <div class="absolute inset-0 bg-dot-ink bg-dot-20 opacity-[0.05]"></div>
          <div class="absolute -top-20 -right-20 w-72 h-72 bg-brand-500/20 rounded-full blur-3xl"></div>

          <div class="relative">
            <h2 class="font-display text-4xl text-white">Start with SEO intelligence that drives leads.</h2>
            <p class="mt-3 text-ink-300 max-w-lg mx-auto">
              Run your first 100-point audit in 3–5 minutes — no credit card, no expiration. Upgrade when you're ready for more.
            </p>
            <div class="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <router-link to="/register" class="btn-brand btn-lg">
                Try Starter free <Icon name="arrow-right" :size="16" />
              </router-link>
              <router-link to="/contact-sales" class="btn btn-lg bg-white/10 text-white border border-white/20 hover:bg-white/20">
                Talk to sales
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { TIERS } from '@/data/pricingTiers';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';
import Tooltip from '../ui/Tooltip.vue';
import CollapsibleSection from '../ui/CollapsibleSection.vue';
import { useUserStore } from '../../stores/userStore';
import { trackBeginCheckout } from '../../analytics/events.js';

usePageMeta(() => ({
  title: 'Pricing — LeadRanks',
  meta: [
    { name: 'description', content: 'Simple, transparent SEO audit pricing. Free starter tier, Pro for freelancers, Agency for white-label client work. Start free, no credit card needed.' },
    { property: 'og:title', content: 'Pricing — LeadRanks' },
    { property: 'og:description', content: 'Simple, transparent SEO audit pricing. Free starter tier, Pro for freelancers, Agency for white-label client work. Start free, no credit card needed.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
  // Built directly from the `faqs`/`tiers` data the page itself renders —
  // never a second, hand-maintained copy that could drift from what's
  // actually visible. Google requires structured data to match real,
  // visible page content, not fabricated markup.
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://leadranks.io/' },
        { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://leadranks.io/pricing' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
    {
      // References the SAME SoftwareApplication entity declared once in
      // static/index.html (shared @id, no @type/name/etc. here) instead of
      // redeclaring a second, competing SoftwareApplication node with a
      // conflicting url/offers -- found live 2026-08-15: two full
      // SoftwareApplication declarations for "LeadRanks" on one page (one
      // generic $0 offer, one real 3-tier AggregateOffer) is exactly the
      // kind of duplicate-entity ambiguity that can make a rich-results
      // parser dedupe/ignore one arbitrarily. This attaches the real
      // pricing data to the one canonical entity instead.
      '@context': 'https://schema.org',
      '@id': 'https://leadranks.io/#software',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '0',
        highPrice: String(tiers.find((t) => t.key === 'agency')?.monthly ?? 199),
        offerCount: String(tiers.filter((t) => !t.customPrice).length),
        offers: tiers
          .filter((t) => !t.customPrice)
          .map((t) => ({
            '@type': 'Offer',
            name: `LeadRanks ${t.name}`,
            price: String(t.monthly),
            priceCurrency: 'USD',
            description: t.desc,
            url: 'https://leadranks.io/pricing',
          })),
      },
    },
  ],
}));

const router = useRouter();
const billing = ref('yearly');
const checkoutLoading = ref(null); // tracks which tier is loading
const checkoutError = ref('');
const checkoutSuccessMessage = ref('');
// Real bug found live 2026-09-02: checkoutError/checkoutSuccessMessage are
// single shared refs, but each tier card in the v-for below renders its
// own <p v-if="checkoutSuccessMessage">...</p> with no per-tier check --
// unlike checkoutLoading, which correctly compares against tier.key. The
// result: clicking one tier's button made the confirmation message show
// up under EVERY tier's button, not just the one that was actually
// clicked. This tracks which tier a message belongs to, same pattern as
// checkoutLoading, so the template can gate on both.
const checkoutMessageTier = ref(null);

const userStore = useUserStore();
const isLoggedIn = computed(() => userStore.isAuthenticated);

const togglePillStyle = computed(() => ({
  left:  billing.value === 'monthly' ? '4px' : 'calc(50% + 2px)',
  right: billing.value === 'monthly' ? 'calc(50% + 2px)' : '4px',
}));

// =========================================================================
// CHECKOUT LOGIC
// =========================================================================

async function checkout(tierKey) {
  checkoutError.value = '';
  checkoutSuccessMessage.value = '';
  checkoutMessageTier.value = tierKey;

  // If not logged in, redirect to register (with tier pre-selection)
  if (!isLoggedIn.value) {
    router.push(`/register?tier=${tierKey}&billing=${billing.value}`);
    return;
  }

  checkoutLoading.value = tierKey;
  try {
    const resp = await fetchWithCSRF('/api/checkout', {
      method: 'POST',
      body: JSON.stringify({
        tier: tierKey,
        billing: billing.value,
      }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      checkoutError.value = err.error || err.detail || 'Checkout failed. Please try again.';
      checkoutLoading.value = null;
      return;
    }

    const data = await resp.json();
    if (data.success && data.url) {
      // GA4 begin_checkout -- subscription path (see useCheckoutButton.js for
      // the one-time credit packs, which report through the same event).
      trackBeginCheckout({ item: 'subscription', tier: tierKey, billing: billing.value });
      window.location.href = data.url;
    } else if (data.success && data.changed_immediately) {
      // Existing subscriber — plan was modified in place server-side
      // (see stripe_endpoints.py's create_checkout_session), no Checkout
      // redirect needed. Refresh the user's tier so the rest of the app
      // (feature gates, the toggle itself) reflects the new plan right away.
      checkoutSuccessMessage.value = data.message || 'Your plan has been updated.';
      checkoutLoading.value = null;
      await userStore.fetchUser();
    } else {
      checkoutError.value = data.error || 'Failed to create checkout session.';
      checkoutLoading.value = null;
    }
  } catch (e) {
    checkoutError.value = 'Network error. Please try again.';
    checkoutLoading.value = null;
  }
}

// =========================================================================
// 4-TIER PRICING MODEL
// =========================================================================

const tiers = TIERS;

const currentPrice = (tier) => billing.value === 'yearly' ? tier.yearly : tier.monthly;

// =========================================================================
// COMPARISON TABLE
// =========================================================================

const comparisonRows = [
  { header: true, label: 'Audits', values: {} },
  { label: 'Full audits', tooltip: '100+ point audits, enforced by tier. Starter is a one-time allotment, not a recurring monthly grant — paid tiers renew every billing cycle.',
    values: { starter: '3 total', professional: '30/mo', agency: '60/mo', enterprise: 'Custom' } },
  { label: 'Competitors per audit', tooltip: 'Side-by-side scoring on the same checks. Enforced on submission.',
    values: { starter: 'Up to 3', professional: 'Up to 3', agency: 'Up to 3', enterprise: 'Unlimited' } },
  { label: 'History retention', tooltip: 'Older reports are automatically cleaned up after this period.',
    values: { starter: '7 days', professional: 'Unlimited', agency: 'Unlimited', enterprise: 'Unlimited' } },

  { header: true, label: 'AI Intelligence', values: {} },
  { label: 'All 17 analysis modules', values: { starter: true, professional: true, agency: true, enterprise: true } },
  { label: 'Core Web Vitals', tooltip: 'LCP, FCP, CLS, TBT, INP on mobile & desktop.',
    values: { starter: true, professional: true, agency: true, enterprise: true } },
  { label: 'SEO Assistant', tooltip: 'Ask questions grounded in your report data — same model for every tier. 10 messages/month on Starter, unlimited on paid plans.',
    values: { starter: '10/mo', professional: 'Unlimited', agency: 'Unlimited', enterprise: 'Unlimited' } },
  { label: 'Auto-competitor discovery', tooltip: 'Automatically find top competitors and analyze keyword gaps from the SERP.',
    values: { starter: false, professional: '5/mo', agency: '10/mo', enterprise: '25/mo' } },

  { header: true, label: 'Prospecting & Lead Gen', values: {} },
  { label: 'Lead credits / month', tooltip: 'Each credit = one company analyzed, scored, and ready to pitch. Average user lands their first client within 20 credits. Credit packs available on paid tiers.',
    values: { starter: false, professional: '20', agency: '50', enterprise: 'Custom' } },
  { label: 'Campaign management', tooltip: 'Group leads into campaigns, track outreach, and export prospect lists.',
    values: { starter: false, professional: true, agency: true, enterprise: true } },
  { label: 'Lead export (CSV/HTML)', values: { starter: false, professional: true, agency: true, enterprise: true } },

  { header: true, label: 'Real Platform Integrations', values: {} },
  { label: 'Google Search Console', tooltip: 'Connect GSC for real organic traffic, keyword positions, and click-through data — not estimates.',
    values: { starter: false, professional: true, agency: true, enterprise: true } },
  { label: 'Google Analytics 4', tooltip: 'Connect GA4 for real conversion data, revenue impact, and traffic source breakdown.',
    values: { starter: false, professional: true, agency: true, enterprise: true } },
  { label: 'GEO readiness', tooltip: 'AI-powered visibility for ChatGPT, Perplexity, and Gemini. Track how AI surfaces your brand.',
    values: { starter: false, professional: true, agency: true, enterprise: true } },

  { header: true, label: 'Platform & Export', values: {} },
  { label: 'PDF export', values: { starter: true, professional: true, agency: true, enterprise: true } },
  { label: 'CSV & JSON export', tooltip: 'Download reports as structured data for your own workflows.',
    values: { starter: false, professional: true, agency: true, enterprise: true } },
  { label: 'ROI projections', tooltip: 'Estimated dollar impact and traffic lift for each SEO fix.',
    values: { starter: true, professional: true, agency: true, enterprise: true } },
  { label: 'White-label reports', tooltip: 'Your logo, colors, domain. No LeadRanks branding.',
    values: { starter: false, professional: false, agency: true, enterprise: true } },
  { label: 'Custom subdomain', values: { starter: false, professional: false, agency: true, enterprise: true } },
  { label: 'Compare mode', tooltip: 'Side-by-side report comparison against competitors.',
    values: { starter: true, professional: true, agency: true, enterprise: true } },
  { label: 'Share link', tooltip: 'Share a public link to any report — no login required for viewers.',
    values: { starter: true, professional: true, agency: true, enterprise: true } },

  { header: true, label: 'Support & Security', values: {} },
  { label: 'Support level', values: { starter: 'Community', professional: 'Priority email', agency: 'Priority email', enterprise: 'Dedicated' } },
  { label: '99.9% uptime target', values: { starter: true, professional: true, agency: true, enterprise: true } },
  { label: 'TLS 1.3 + bcrypt', values: { starter: true, professional: true, agency: true, enterprise: true } },
];

// =========================================================================
// FAQ
// =========================================================================

const faqs = [
  { question: 'Can I switch plans at any time?',
    answer: 'Yes. Upgrade or downgrade instantly — prorated to the day. No data loss, no migration, no awkward conversations.' },
  { question: 'What counts as one audit credit?',
    answer: 'Each full analysis — from URL submission through report render — uses one credit. Re-opening a saved report, viewing history, or downloading an existing report is always free and unlimited.' },
  { question: 'What happens if I hit my audit limit?',
    answer: 'We let you finish any in-progress audit, then pause new submissions. On Pro or Agency, your limit resets automatically on your billing date — you can also upgrade mid-cycle (prorated) if you need more sooner. Starter\'s 5 audits are a one-time allotment, not monthly — there\'s no reset; upgrading is the only way to unlock more.' },
  { question: 'Is Starter really free with no credit card?',
    answer: 'Yes — 5 full audits, every module, forever. No credit card, no expiration, no "trial ending" email. We\'re confident you\'ll see the value and upgrade when you\'re ready, but there\'s zero pressure.' },
  { question: 'How does the 14-day Pro/Agency trial work?',
    answer: 'Full access to all features for 14 days. Enter billing info to start — if you cancel before day 14, you won\'t be charged. If you love it (you will), your subscription continues at the plan rate.' },
  { question: 'Do you offer discounts for non-profits or education?',
    answer: 'Yes — 40% off Pro or Agency for verified non-profits and educational institutions. Reach out to sales with your proof of status.' },
  { question: 'Can I cancel anytime?',
    answer: 'Anytime. No long-term contracts on Starter, Pro, or Agency. Enterprise plans can be annual with custom terms, but we\'ll never lock you in against your will.' },
  { question: 'What payment methods do you accept?',
    answer: 'All major cards (Visa, MasterCard, Amex), PayPal, and bank transfer/invoicing for Enterprise plans. We use Stripe for secure processing — your payment details never touch our servers.' },
  { question: 'Is my data secure?',
    answer: 'Passwords are hashed with bcrypt. All traffic is TLS 1.3. Your audit data is encrypted at rest and never shared with third parties or used to train models. Enterprise plans include a DPA for GDPR compliance.' },
];
</script>
